import styled from "@emotion/styled";
import type { HTMLAttributes, RefObject } from "react";

import { zIndexes } from "../utilities";
import type { CollisionInsertPosition } from "./types";

type SortableInsertPosition = Exclude<CollisionInsertPosition, "inside">;

type Orientation = "horizontal" | "vertical";

type Offset = {
  top: number;
  left: number;
};

type DropIndicatorColor = "primary" | "secondary" | "warning";

const Indicator = styled.div<{ $orientation: Orientation; $color: DropIndicatorColor }>`
  z-index: ${zIndexes.composableNodeDropIndicator};
  background: ${({ theme, $color }) => theme[$color]};

  ${({ $orientation }) =>
    $orientation === "horizontal"
      ? `
      border-top: 0;
    `
      : `
      border-left: 0;
    `}

  &:after {
    content: "";
    position: absolute;
    z-index: -1;
    background: ${({ theme }) => theme.surface};
    border: solid 2px ${({ theme, $color }) => theme[$color]};
    border-radius: 10px;
    height: 10px;
    width: 10px;

    ${({ $orientation }) =>
      $orientation === "horizontal"
        ? `
      top: -5px;
      left: -2px;
      right: 0;
    `
        : `
      left: -5px;
      top: -3px;
      bottom: 0;
    `}
  }
`;

const getDropIndicatorPositionStyles = (
  domElement: RefObject<HTMLElement | null>,
  insertPosition: SortableInsertPosition,
  orientation: Orientation,
  offset?: Offset,
  inLayersPanel?: boolean,
) => {
  const {
    top = 0,
    left = 0,
    right = 0,
    bottom = 0,
    height = 0,
    width = 0,
  } = domElement.current?.getBoundingClientRect() || {};
  const leftOffset = offset?.left ?? 0;
  const topOffset = offset?.top ?? 0;
  const size = 5;

  return orientation === "vertical"
    ? {
        top: `${(insertPosition === "insertTop" ? top : bottom - size) - topOffset}px`,
        left: `${left - leftOffset}px`,
        width: `${inLayersPanel ? width + leftOffset : width}px`,
        height: `${size}px`,
      }
    : {
        top: `${top - topOffset}px`,
        left: `${(insertPosition === "insertLeft" ? left : right - size) - leftOffset}px`,
        width: `${size}px`,
        height: `${height}px`,
      };
};

type Props = HTMLAttributes<HTMLDivElement> & {
  domRef: RefObject<HTMLElement | null>;
  insertPosition: SortableInsertPosition;
  offset?: Offset;
  inLayersPanel?: boolean;
  color?: DropIndicatorColor;
};

export const DropIndicator = ({ domRef, insertPosition, offset, inLayersPanel, color, ...domAttrs }: Props) => {
  const orientation = ["insertTop", "insertBottom"].includes(insertPosition) ? "vertical" : "horizontal";

  return (
    <Indicator
      {...domAttrs}
      $orientation={orientation}
      $color={color ?? "secondary"}
      style={{
        ...getDropIndicatorPositionStyles(domRef, insertPosition, orientation, offset, inLayersPanel),
        position: inLayersPanel ? "fixed" : "absolute",
        ...domAttrs?.style,
      }}
    />
  );
};
